/* eslint-disable @typescript-eslint/no-explicit-any */
import { ref, watch } from 'vue';
import { defineStore } from 'pinia';
import useAxios from '@/libs/axios';
import createFeatureAxios from '@/libs/featureAxios';
import Plugin from '@/models/plugin/plugin';
import Pagination from '@/models/pagination';
import eventBus from '@/eventBus/eventBus';
import InstagramLiveMetadata from '@/models/plugin/instagramLiveMetadata';
import InstagramLiveMetadataMetrics from '@/models/plugin/instagramLiveMetadataMetrics';
import UserBio from '@/models/user/bio/userBio';
import {
  CartLineEvidence,
  InstagramLiveAIMetadataModel,
  parseCartLineEvidence,
} from '@/types/aiShoppingCart';
import InstagramLiveMessage from '@/models/plugin/instagramLiveMessage';
import InstagramLiveUser from '@/models/plugin/instagramLiveUser';
import InstagramLiveStatus from '@/models/plugin/instagramLiveStatus';
import InstagramPluginModerator from '@/models/plugin/instagramPluginModerator';
import InstagramPlugin from '@/models/plugin/instagramPlugin';
import FacebookPlugin from '@/models/plugin/facebookPlugin';
import FacebookLiveMetadata from '@/models/plugin/facebookLiveMetadata';
import FacebookLiveMessage from '@/models/plugin/facebookLiveMessage';
import FacebookLiveUser from '@/models/plugin/facebookLiveUser';
import FacebookLiveStatus from '@/models/plugin/facebookLiveStatus';
import {
  GetTranscriptionRequest,
  GetTranscriptionResponse,
  TranscribeVideoRequest,
  TranscribeVideoResponse,
  InstagramMediaResponse,
  AnalyzeTranscriptionResponse,
  MediaTranscriptionJobStatus,
  MediaTranscriptionAnalysis,
  MediaTranscriptionProducts,
} from '@/models/plugin/transcription';

interface InstagramLiveMetadataResponse {
  data: InstagramLiveMetadata[];
  pagination: Pagination;
}

interface InstagramLiveMetadataMetricsResponse {
  data: InstagramLiveMetadataMetrics[];
}

interface InstagramLiveMessageResponse {
  data: InstagramLiveMessage[];
  pagination: Pagination;
  missingPayment: boolean;
}

interface InstagramLiveUserResponse {
  data: InstagramLiveUser[];
  pagination: Pagination;
}

interface InstagramLiveStatusResponse {
  data: InstagramLiveStatus[];
  pagination: Pagination;
}

interface FacebookLiveMetadataResponse {
  data: FacebookLiveMetadata[];
  pagination: Pagination;
}

interface FacebookLiveMessageResponse {
  data: FacebookLiveMessage[];
  pagination: Pagination;
  missingPayment: boolean;
}

interface FacebookLiveUserResponse {
  data: FacebookLiveUser[];
  pagination: Pagination;
}

interface FacebookLiveStatusResponse {
  data: FacebookLiveStatus[];
  pagination: Pagination;
}

export default defineStore('pluginStore', () => {
  const axios = useAxios();

  /*
    State
  */
  const pluginStore = ref({
    csrf: '',
    plugins: {
      data: [] as Plugin[],
      pagination: {} as Pagination,
    },
  });

  /*
    Actions
  */
  // Reset the whole store and remove it from localStorage
  // after user logout
  const resetStore = () => {
    localStorage.removeItem('pluginStore');
  };

  const pluginLiveMessageReply = async (params: {
    pluginId: string,
    pluginType: string,
    commentId: string,
    commentAnswer: string,
  }): Promise<boolean> => {
    const config = {
      headers: {
        'Accept-Language': 'pt-BR',
      },
    };

    const payload = {
      plugin_id: params.pluginId,
      plugin_type: params.pluginType,
      comment_id: params.commentId,
      comment_answer: params.commentAnswer,
    };

    try {
      const response = await axios.post('/plugins/live/messages/reply', payload, config);

      if (response.status !== 200) { // No content
        throw new Error('Erro ao responder mensagem. Recarregue a página e tente novamente.');
      }
      return true;
    } catch (error: any) {
      if (error.response && error.response.status !== 401) {
        throw error;
      } else if (error.message) {
        throw new Error(`Erro na solicitação: ${error.message}`);
      } else {
        throw new Error('Erro desconhecido ao atualizar live metadata.');
      }
    }
  };

  const pluginGetInstagramLiveStatus = async (params: {
    pluginId: string,
  }): Promise<InstagramLiveStatusResponse> => {
    const config = {
      headers: {
        'Accept-Language': 'pt-BR',
      },
    };

    const payload = {
      plugin_id: params.pluginId,
    };

    try {
      const response = await axios.post('/plugins/instagram/live/status', payload, config);

      if (response.status === 204) { // No content
        return {
          data: [] as InstagramLiveStatus[],
          pagination: {} as Pagination,
        };
      }

      const { columns, rows, pagination } = response.data;

      if (!columns || !rows) {
        return {
          data: [] as InstagramLiveStatus[],
          pagination: {} as Pagination,
        };
      }

      return {
        data: InstagramLiveStatus.fromApiResponse(columns, rows),
        pagination,
      };
    } catch (error: any) {
      console.error('Error from API call:', error);
      if (error.response && error.response.status !== 401) {
        throw error;
      } else if (error.message) {
        throw new Error(`Erro na solicitação: ${error.message}`);
      } else {
        throw new Error('Erro desconhecido ao atualizar live metadata.');
      }
    }
  };

  const pluginRefreshLiveMetadata = async (params: {
    liveUserId: string,
    liveId: string,
    type: string,
  }): Promise<boolean> => {
    const config = {
      headers: {
        'Accept-Language': 'pt-BR',
      },
    };

    const payload = {
      live_user_id: params.liveUserId,
      live_id: params.liveId,
      type: params.type,
    };

    try {
      const response = await axios.put('/plugins/live-metadata', payload, config);
      return response.status === 200;
    } catch (error: any) {
      if (error.response && error.response.status !== 401) {
        throw error;
      } else if (error.message) {
        throw new Error(`Erro na solicitação: ${error.message}`);
      } else {
        throw new Error('Erro desconhecido ao atualizar live metadata.');
      }
    }
  };

  const pluginGetInstagramLiveUsers = async (params: {
    pluginId: string,
    resourceId: string,
    page: number,
    limit: number,
    orderBy: string,
    order: number,
    searchParam: string,
  }): Promise<InstagramLiveUserResponse> => {
    const config = {
      headers: {
        'Accept-Language': 'pt-BR',
      },
    };

    const payload = {
      plugin_id: params.pluginId,
      resource_id: params.resourceId,
      page: params.page,
      limit: params.limit,
      order_by: params.orderBy,
      order: params.order,
      search_param: params.searchParam,
    };

    try {
      const response = await axios.post('/plugins/instagram/live/users', payload, config);
      // console.debug('response', response);
      if (response.status === 204) { // No content
        return {
          data: [] as InstagramLiveUser[],
          pagination: {} as Pagination,
        };
      }

      if (response.status !== 200) {
        throw new Error(`Erro ao buscar usuários: ${response.statusText}`);
      }

      const {
        columns, rows, pagination: pageObj,
      } = response.data;

      const pagination = new Pagination({
        total: pageObj.total,
        perPage: pageObj.per_page,
        currentPage: pageObj.current_page,
        lastPage: pageObj.last_page,
      });

      return {
        data: InstagramLiveUser.fromApiResponse(columns, rows),
        pagination,
      };
    } catch (error: any) {
      if (error.response && error.response.status !== 401) {
        throw error;
      } else if (error.message) {
        throw new Error(`Erro na solicitação: ${error.message}`);
      } else {
        throw new Error('Erro desconhecido ao buscar metadados.');
      }
    }
  };

  // Server-side peak aggregation — returns the busiest 10-minute bucket of
  // message activity for a live, computed via MongoDB aggregation so it works
  // for lives of any size. Caching + ownership checks happen server-side.
  const pluginGetLiveMessagesPeak = async (params: {
    pluginId: string,
    resourceId: string,
    platform: 'instagram' | 'facebook',
  }): Promise<{ peakCount: number; peakBucketStart: Date | null }> => {
    const config = {
      headers: { 'Accept-Language': 'pt-BR' },
    };
    const payload = {
      plugin_id: params.pluginId,
      resource_id: params.resourceId,
    };
    try {
      const response = await axios.post(
        `/plugins/${params.platform}/live/messages/peak`,
        payload,
        config,
      );
      if (response.status !== 200) {
        throw new Error(`Erro ao buscar pico: ${response.statusText}`);
      }
      const data = response.data || {};
      return {
        peakCount: Number(data.peak_count ?? 0),
        peakBucketStart: data.peak_bucket_start ? new Date(data.peak_bucket_start) : null,
      };
    } catch (error: any) {
      if (error.response && error.response.status !== 401) {
        throw error;
      }
      throw new Error(`Erro ao buscar pico: ${error?.message ?? 'desconhecido'}`);
    }
  };

  const pluginGetInstagramLiveMessages = async (params: {
    pluginId: string,
    resourceId: string,
    page: number,
    limit: number,
    orderBy: string,
    order: number,
    searchParam: string,
    fullRefresh?: boolean,
    accountName?: string,
  }): Promise<InstagramLiveMessageResponse> => {
    const config = {
      headers: {
        'Accept-Language': 'pt-BR',
      },
    };

    // If full refresh, we call SYNC api first then we call the messages api
    if (params.fullRefresh) {
      const response = await axios.post('/plugins/sync-live-status', {
        media_id: params.resourceId,
        account_name: params.accountName,
      });
      console.debug('response', response);
    }

    let { orderBy } = params;
    let { order } = params;

    if (params.order === 2) {
      orderBy = 'userName';
      order = 1;
    }

    const payload = {
      plugin_id: params.pluginId,
      resource_id: params.resourceId,
      page: params.page,
      limit: params.limit,
      order_by: orderBy,
      order,
      search_param: params.searchParam,
    };

    try {
      const response = await axios.post('/plugins/instagram/live/messages', payload, config);
      // console.debug('response', response);
      if (response.status === 204) { // No content
        return {
          data: [] as InstagramLiveMessage[],
          pagination: {} as Pagination,
          missingPayment: true,
        };
      }

      if (response.status !== 200) {
        throw new Error(`Erro ao buscar mensages: ${response.statusText}`);
      }

      const {
        columns, rows, pagination: pageObj, missingPayment,
      } = response.data;

      const pagination = new Pagination({
        total: pageObj.total,
        perPage: pageObj.per_page,
        currentPage: pageObj.current_page,
        lastPage: pageObj.last_page,
      });

      return {
        data: InstagramLiveMessage.fromApiResponse(columns, rows),
        pagination,
        missingPayment,
      };
    } catch (error: any) {
      if (error.response && error.response.status !== 401) {
        throw error;
      } else if (error.message) {
        throw new Error(`Erro na solicitação: ${error.message}`);
      } else {
        throw new Error('Erro desconhecido ao buscar metadados.');
      }
    }
  };

  const pluginGetSharedWithMeLiveMetadatas = async (params: {
    page: number,
    limit: number,
    orderBy: string,
    order: number,
  }): Promise<InstagramLiveMetadataResponse> => {
    const config = {
      headers: {
        'Accept-Language': 'pt-BR',
      },
    };

    const payload = {
      page: params.page,
      limit: params.limit,
      order_by: params.orderBy,
      order: params.order,
    };

    try {
      const response = await axios.post('/plugins/live/shared/with-me', payload, config);
      // console.debug('response', response);
      if (response.status === 204) { // No content
        return {
          data: [] as InstagramLiveMetadata[],
          pagination: {} as Pagination,
        };
      }

      if (response.status !== 200) {
        throw new Error(`Erro ao buscar metadados: ${response.statusText}`);
      }

      const { columns, rows, pagination } = response.data;

      return {
        data: InstagramLiveMetadata.fromApiResponse(columns, rows),
        pagination,
      };
    } catch (error: any) {
      if (error.response && error.response.status !== 401) {
        throw error;
      } else if (error.message) {
        throw new Error(`Erro na solicitação: ${error.message}`);
      } else {
        throw new Error('Erro desconhecido ao buscar metadados.');
      }
    }
  };

  const pluginGetLiveMetadatasMetrics = async (params: {
    pluginId?: string,
    pluginType?: string,
  }): Promise<InstagramLiveMetadataMetricsResponse> => {
    const config = {
      headers: {
        'Accept-Language': 'pt-BR',
      },
    };

    let payload = {};

    if (params.pluginId && params.pluginType) {
      payload = {
        plugin_id: params.pluginId,
        plugin_type: params.pluginType,
      };
    }

    try {
      const response = await axios.post('/plugins/live/metadata/metrics', payload, config);
      // console.debug('response', response);
      if (response.status === 204) { // No content
        return {
          data: InstagramLiveMetadataMetrics.fromApiResponse([], []),
        };
      }

      if (response.status !== 200) {
        throw new Error(`Erro ao buscar metadados: ${response.statusText}`);
      }

      const { columns, row } = response.data;

      return { data: InstagramLiveMetadataMetrics.fromApiResponse(columns, [row]) };
    } catch (error: any) {
      if (error.response && error.response.status !== 401) {
        throw error;
      } else if (error.message) {
        throw new Error(`Erro na solicitação: ${error.message}`);
      } else {
        throw new Error('Erro desconhecido ao buscar metadados.');
      }
    }
  };

  const pluginGetInstagramLiveMetadatas = async (params: {
    pluginId: string,
    page: number,
    limit: number,
    orderBy: string,
    order: number,
    resourceId?: string,
    moderator?: boolean,
  }): Promise<InstagramLiveMetadataResponse> => {
    const config = {
      headers: {
        'Accept-Language': 'pt-BR',
      },
    };

    const payload: Record<string, unknown> = {
      plugin_id: params.pluginId,
      page: params.page,
      limit: params.limit,
      order_by: params.orderBy,
      order: params.order,
    };

    if (params.moderator) {
      payload.moderator = params.moderator;
    }

    if (params.resourceId) {
      payload.resource_id = params.resourceId;
    }

    try {
      const response = await axios.post('/plugins/instagram/live/metadata', payload, config);
      // console.debug('response', response);
      if (response.status === 204) { // No content
        return {
          data: [] as InstagramLiveMetadata[],
          pagination: {} as Pagination,
        };
      }

      if (response.status !== 200) {
        throw new Error(`Erro ao buscar metadados: ${response.statusText}`);
      }

      const { columns, rows, pagination } = response.data;

      return {
        data: InstagramLiveMetadata.fromApiResponse(columns, rows),
        pagination: new Pagination({
          total: pagination.total,
          perPage: pagination.per_page,
          currentPage: pagination.current_page,
          lastPage: pagination.last_page,
        }),
      };
    } catch (error: any) {
      if (error.response && error.response.status !== 401) {
        throw error;
      } else if (error.message) {
        throw new Error(`Erro na solicitação: ${error.message}`);
      } else {
        throw new Error('Erro desconhecido ao buscar metadados.');
      }
    }
  };

  const pluginGetFacebookLiveMetadatas = async (params: {
    pluginId: string,
    page: number,
    limit: number,
    orderBy: string,
    order: number,
    resourceId?: string,
    moderator?: boolean,
  }): Promise<FacebookLiveMetadataResponse> => {
    const config = {
      headers: {
        'Accept-Language': 'pt-BR',
      },
    };

    const payload: Record<string, unknown> = {
      plugin_id: params.pluginId,
      page: params.page,
      limit: params.limit,
      order_by: params.orderBy,
      order: params.order,
    };

    if (params.moderator) {
      payload.moderator = params.moderator;
    }

    if (params.resourceId) {
      payload.resource_id = params.resourceId;
    }

    try {
      const response = await axios.post('/plugins/facebook/live/metadata', payload, config);

      if (response.status === 204) {
        return {
          data: [] as FacebookLiveMetadata[],
          pagination: {} as Pagination,
        };
      }

      if (response.status !== 200) {
        throw new Error(`Erro ao buscar metadados: ${response.statusText}`);
      }

      const { columns, rows, pagination } = response.data;

      return {
        data: FacebookLiveMetadata.fromApiResponse(columns, rows),
        pagination: new Pagination({
          total: pagination.total,
          perPage: pagination.per_page,
          currentPage: pagination.current_page,
          lastPage: pagination.last_page,
        }),
      };
    } catch (error: any) {
      if (error.response && error.response.status !== 401) {
        throw error;
      } else if (error.message) {
        throw new Error(`Erro na solicitação: ${error.message}`);
      } else {
        throw new Error('Erro desconhecido ao buscar metadados.');
      }
    }
  };

  const pluginGetLiveMetadatas = async (params: {
    pluginId: string,
    pluginType: string,
    page: number,
    limit: number,
    orderBy: string,
    order: number,
    resourceId?: string,
    moderator?: boolean,
  }) => {
    if (params.pluginType === 'facebook') {
      return pluginGetFacebookLiveMetadatas({
        pluginId: params.pluginId,
        page: params.page,
        limit: params.limit,
        orderBy: params.orderBy,
        order: params.order,
        resourceId: params.resourceId,
        moderator: params.moderator,
      });
    }
    return pluginGetInstagramLiveMetadatas({
      pluginId: params.pluginId,
      page: params.page,
      limit: params.limit,
      orderBy: params.orderBy,
      order: params.order,
      resourceId: params.resourceId,
      moderator: params.moderator,
    });
  };

  const pluginGetFacebookLiveMessages = async (params: {
    pluginId: string,
    resourceId: string,
    page: number,
    limit: number,
    orderBy: string,
    order: number,
    searchParam?: string,
  }): Promise<FacebookLiveMessageResponse> => {
    const config = {
      headers: {
        'Accept-Language': 'pt-BR',
      },
    };

    let { orderBy } = params;
    let { order } = params;

    if (params.order === 2) {
      orderBy = 'userName';
      order = 1;
    }

    const payload: Record<string, unknown> = {
      plugin_id: params.pluginId,
      resource_id: params.resourceId,
      page: params.page,
      limit: params.limit,
      order_by: orderBy,
      order,
    };

    if (params.searchParam) {
      payload.search_param = params.searchParam;
    }

    try {
      const response = await axios.post('/plugins/facebook/live/messages', payload, config);

      if (response.status === 204) {
        return {
          data: [] as FacebookLiveMessage[],
          pagination: {} as Pagination,
          missingPayment: true,
        };
      }

      if (response.status !== 200) {
        throw new Error(`Erro ao buscar mensagens: ${response.statusText}`);
      }

      const {
        columns, rows, pagination: pageObj, missingPayment,
      } = response.data;

      const pagination = new Pagination({
        total: pageObj.total,
        perPage: pageObj.per_page,
        currentPage: pageObj.current_page,
        lastPage: pageObj.last_page,
      });

      return {
        data: FacebookLiveMessage.fromApiResponse(columns, rows),
        pagination,
        missingPayment,
      };
    } catch (error: any) {
      if (error.response && error.response.status !== 401) {
        throw error;
      } else if (error.message) {
        throw new Error(`Erro na solicitação: ${error.message}`);
      } else {
        throw new Error('Erro desconhecido ao buscar mensagens.');
      }
    }
  };

  const pluginGetFacebookLiveUsers = async (params: {
    pluginId: string,
    resourceId: string,
    page: number,
    limit: number,
    orderBy: string,
    order: number,
    searchParam?: string,
  }): Promise<FacebookLiveUserResponse> => {
    const config = {
      headers: {
        'Accept-Language': 'pt-BR',
      },
    };

    const payload: Record<string, unknown> = {
      plugin_id: params.pluginId,
      resource_id: params.resourceId,
      page: params.page,
      limit: params.limit,
      order_by: params.orderBy,
      order: params.order,
    };

    if (params.searchParam) {
      payload.search_param = params.searchParam;
    }

    try {
      const response = await axios.post('/plugins/facebook/live/users', payload, config);

      if (response.status === 204) {
        return {
          data: [] as FacebookLiveUser[],
          pagination: {} as Pagination,
        };
      }

      if (response.status !== 200) {
        throw new Error(`Erro ao buscar usuários: ${response.statusText}`);
      }

      const {
        columns, rows, pagination: pageObj,
      } = response.data;

      const pagination = new Pagination({
        total: pageObj.total,
        perPage: pageObj.per_page,
        currentPage: pageObj.current_page,
        lastPage: pageObj.last_page,
      });

      return {
        data: FacebookLiveUser.fromApiResponse(columns, rows),
        pagination,
      };
    } catch (error: any) {
      if (error.response && error.response.status !== 401) {
        throw error;
      } else if (error.message) {
        throw new Error(`Erro na solicitação: ${error.message}`);
      } else {
        throw new Error('Erro desconhecido ao buscar usuários.');
      }
    }
  };

  const pluginGetFacebookLiveStatus = async (params: {
    pluginId: string,
  }): Promise<FacebookLiveStatusResponse> => {
    const config = {
      headers: {
        'Accept-Language': 'pt-BR',
      },
    };

    const payload = {
      plugin_id: params.pluginId,
    };

    try {
      const response = await axios.post('/plugins/facebook/live/status', payload, config);

      if (response.status === 204) {
        return {
          data: [] as FacebookLiveStatus[],
          pagination: {} as Pagination,
        };
      }

      const { columns, rows, pagination } = response.data;

      if (!columns || !rows) {
        return {
          data: [] as FacebookLiveStatus[],
          pagination: {} as Pagination,
        };
      }

      return {
        data: FacebookLiveStatus.fromApiResponse(columns, rows),
        pagination,
      };
    } catch (error: any) {
      console.error('Error from API call:', error);
      if (error.response && error.response.status !== 401) {
        throw error;
      } else if (error.message) {
        throw new Error(`Erro na solicitação: ${error.message}`);
      } else {
        throw new Error('Erro desconhecido ao buscar status.');
      }
    }
  };

  const pluginGenerateAILiveShoppingCarts = async (params: {
    accountId: string,
    mediaId: string,
  }): Promise<boolean> => {
    const config = {
      headers: {
        'Accept-Language': 'pt-BR',
      },
    };

    const payload = {
      account_id: params.accountId,
      media_id: params.mediaId,
    };

    try {
      const response = await axios.post('/plugins/live/ai/shoppingcart/create', payload, config);
      if (response.status === 204) { // No content
        throw new Error('Erro ao gerar carrinho de compras.');
      }

      if (response.status !== 200) {
        throw new Error(`Erro ao buscar metadados: ${response.statusText}`);
      }

      return true;
    } catch (error: any) {
      if (error.response && error.response.status !== 401) {
        throw error;
      } else if (error.message) {
        throw new Error(`Erro na solicitação: ${error.message}`);
      } else {
        throw new Error('Erro desconhecido ao buscar metadados.');
      }
    }
  };

  // pluginEditLiveProduct patches one product on a live's
  // `mediaTranscriptionProducts` doc via Go's proxy (which relays to
  // the Python admin). Returns the patched product so the FE can
  // splice it into local state without re-fetching the whole AI
  // metadata payload. Undefined fields are dropped from the body so
  // the seller editing JUST the price doesn't accidentally wipe the
  // image url.
  const pluginEditLiveProduct = async (params: {
    mediaId: string;
    pluginId: string;
    productId: string;
    label?: string;
    price?: number | null;
    quantity?: number | null;
    imageUrl?: string | null;
    description?: string | null;
    // [sku-stock] Stock per variant — one pair of the 37, two of the 38 —
    // which the single `quantity` above cannot express. The whole
    // catalogue travels, and `quantity: null` on a row means "unknown",
    // which is NOT zero: that variant falls back to the product's pool.
    skus?: { sku: string; quantity: number | null }[];
  }): Promise<{ ok: boolean; product: Record<string, unknown> | null }> => {
    const body: Record<string, unknown> = {
      media_id: params.mediaId,
      plugin_id: params.pluginId,
      product_id: params.productId,
    };
    if (params.label !== undefined) body.label = params.label;
    if (params.price !== undefined) body.price = params.price;
    if (params.quantity !== undefined) body.quantity = params.quantity;
    if (params.imageUrl !== undefined) body.image_url = params.imageUrl;
    if (params.description !== undefined) body.description = params.description;
    if (params.skus !== undefined) body.skus = params.skus;

    const response = await axios.put('/plugins/live/products/edit', body, {
      headers: { 'Accept-Language': 'pt-BR' },
    });
    if (response.status !== 200) {
      throw new Error(`Erro ao salvar: ${response.statusText}`);
    }
    return response.data;
  };

  // pluginGetBillingConfig + pluginUpdateBillingConfig back the
  // seller's "Dados de cobrança" tab. The PUT body is wholesale —
  // the backend overwrites the subdoc — so the FE form should be
  // initialized from the GET payload before any input touches it.
  // Both endpoints are session-auth and ownership-checked on the
  // backend; the FE just relays whatever the user typed.
  type BillingConfigPix = {
    key?: string;
    key_type?: string;
    bank_name?: string;
    branch?: string;
    account_holder?: string;
  };
  type BillingConfig = {
    payment_instructions?: string;
    shipping_info?: string;
    exchange_info?: string;
    pix?: BillingConfigPix;
    updated_at?: string;
    updated_by_user_uuid?: string;
  };
  const pluginGetBillingConfig = async (pluginId: string): Promise<BillingConfig> => {
    const response = await axios.get(`/plugins/${pluginId}/billing-config`, {
      headers: { 'Accept-Language': 'pt-BR' },
    });
    if (response.status !== 200) {
      throw new Error(`Erro ao carregar dados de cobrança: ${response.statusText}`);
    }
    return (response.data?.billing_config || {}) as BillingConfig;
  };
  const pluginUpdateBillingConfig = async (
    pluginId: string,
    config: BillingConfig,
  ): Promise<BillingConfig> => {
    const response = await axios.put(`/plugins/${pluginId}/billing-config`, config, {
      headers: { 'Accept-Language': 'pt-BR' },
    });
    if (response.status !== 200) {
      throw new Error(`Erro ao salvar dados de cobrança: ${response.statusText}`);
    }
    return (response.data?.billing_config || {}) as BillingConfig;
  };

  // pluginGetCartsAggregate powers the dashboard Carrinhos tab.
  // One row per live with at least one buyer cart, revenue split
  // by status bucket so the seller can see "what's pending vs.
  // paid vs. cancelled" at a glance.
  type PluginCartLiveRow = {
    media_id: string;
    media_timestamp?: string;
    cart_count: number;
    revenue_paid: number;
    revenue_pending: number;
    revenue_open: number;
    revenue_cancelled: number;
    revenue_total: number;
  };
  const pluginGetCartsAggregate = async (
    pluginId: string,
  ): Promise<{ accountId: string; lives: PluginCartLiveRow[] }> => {
    const response = await axios.post(`/plugins/${pluginId}/carts/aggregate`, {}, {
      headers: { 'Accept-Language': 'pt-BR' },
    });
    if (response.status !== 200) {
      throw new Error(`Erro ao carregar carrinhos: ${response.statusText}`);
    }
    return {
      accountId: response.data?.account_id || '',
      lives: (response.data?.lives || []) as PluginCartLiveRow[],
    };
  };

  // pluginGetLiveCartStatuses + pluginSetLiveCartStatus back the
  // seller-facing cart status workflow (Carrinhos tab pills +
  // action buttons). List is POST so the body shape stays
  // identical to set — both relay through Go to the agent-controller.
  type CartStatusRow = {
    pluginId: string;
    mediaId: string;
    buyerUserId: string;
    status: string;
    createdAt?: string;
    updatedAt?: string;
    paymentRequestedAt?: string;
    paidAt?: string;
    cancelledAt?: string;
    cancelReason?: string;
    buyerMarkedPaidAt?: string;
    buyerMarkedPaid?: boolean;
  };
  const pluginGetLiveCartStatuses = async (params: {
    mediaId: string;
    pluginId: string;
  }): Promise<CartStatusRow[]> => {
    const response = await axios.post('/plugins/live/cart-status/list', {
      media_id: params.mediaId,
      plugin_id: params.pluginId,
    }, { headers: { 'Accept-Language': 'pt-BR' } });
    if (response.status !== 200) {
      throw new Error(`Erro ao carregar status: ${response.statusText}`);
    }
    return (response.data?.statuses || []) as CartStatusRow[];
  };
  const pluginSetLiveCartStatus = async (params: {
    mediaId: string;
    pluginId: string;
    buyerUserId: string;
    status: 'created' | 'request_payment' | 'paid' | 'cancelled';
    reason?: string;
  }): Promise<{ ok: boolean; status: string }> => {
    const response = await axios.post('/plugins/live/cart-status/set', {
      media_id: params.mediaId,
      plugin_id: params.pluginId,
      buyer_user_id: params.buyerUserId,
      status: params.status,
      reason: params.reason || '',
    }, { headers: { 'Accept-Language': 'pt-BR' } });
    if (response.status !== 200) {
      throw new Error(`Erro ao atualizar status: ${response.statusText}`);
    }
    return response.data;
  };

  // pluginAdjustLiveCart applies a seller-driven cart mutation on
  // behalf of a buyer. Same wire format the buyer-portal endpoint
  // uses; the seller-side route just swaps cookie auth for the
  // session token. Operation values: "increment" | "decrement" |
  // "remove".
  const pluginAdjustLiveCart = async (params: {
    mediaId: string;
    pluginId: string;
    buyerUserId: string;
    buyerUserName?: string;
    productCode: string;
    /**
     * [variant-row] WHICH variant of that código the mutation is for. The
     * cart holds one line per variant, so a buyer who took the 38 and the 40
     * of one model has two lines and the código alone addresses neither —
     * without these the backend would change whichever came first and the
     * seller would watch the wrong size move. Omit both to mean "every line
     * of this código for this buyer", which is what a product with no
     * variants needs.
     */
    size?: string;
    variantColor?: string;
    operation: 'increment' | 'decrement' | 'remove';
  }): Promise<{ ok: boolean; intent_count?: number }> => {
    const body = {
      media_id: params.mediaId,
      plugin_id: params.pluginId,
      buyer_user_id: params.buyerUserId,
      buyer_user_name: params.buyerUserName || '',
      product_code: params.productCode,
      size: params.size || '',
      variant_color: params.variantColor || '',
      operation: params.operation,
    };
    const response = await axios.post('/plugins/live/carts/adjust', body, {
      headers: { 'Accept-Language': 'pt-BR' },
    });
    if (response.status !== 200) {
      throw new Error(`Erro ao ajustar carrinho: ${response.statusText}`);
    }
    return response.data;
  };

  // pluginGetCartLineEvidence loads the evidence behind one cart line
  // (the low_confidence inspect modal): the messages that created the
  // purchase plus the transcript around the purchase moment.
  const pluginGetCartLineEvidence = async (params: {
    mediaId: string;
    pluginId: string;
    buyerUserId: string;
    buyerUserName?: string;
    productId: string;
  }): Promise<CartLineEvidence> => {
    const body = {
      media_id: params.mediaId,
      plugin_id: params.pluginId,
      buyer_user_id: params.buyerUserId,
      buyer_user_name: params.buyerUserName || '',
      product_id: params.productId,
    };
    const response = await axios.post('/plugins/live/carts/evidence', body, {
      headers: { 'Accept-Language': 'pt-BR' },
    });
    if (response.status !== 200) {
      throw new Error(`Erro ao carregar evidências: ${response.statusText}`);
    }
    return parseCartLineEvidence(response.data);
  };

  const pluginGetAILiveShoppingCarts = async (params: {
  accountId: string;
  mediaId: string;
}): Promise<{ data: InstagramLiveAIMetadataModel | null }> => {
    const config = {
      headers: {
        'Accept-Language': 'pt-BR',
      },
    };

    const payload = {
      account_id: params.accountId,
      media_id: params.mediaId,
    };

    try {
      const response = await axios.post('/plugins/live/ai/shoppingcart', payload, config);

      if (response.status === 204) {
        return { data: null };
      }

      if (response.status !== 200) {
        throw new Error(`Erro ao buscar dados: ${response.statusText}`);
      }

      const { columns, row } = response.data;
      return {
        data: InstagramLiveAIMetadataModel.fromApiResponse(columns, [row])[0],
      };
    } catch (error: any) {
      if (error.response && error.response.status !== 401) {
        throw error;
      } else if (error.message) {
        throw new Error(`Erro na solicitação: ${error.message}`);
      } else {
        throw new Error('Erro desconhecido ao buscar metadados.');
      }
    }
  };

  const pluginsDoUpdateModerators = async (params: {
    resourceId: string,
    platform: string,
    moderatorsToAdd: InstagramPluginModerator[] | null,
    emailsToRemove: string[] | null,
  }) => {
    const config = {
      headers: {
        'Accept-Language': 'pt-BR',
      },
    };

    if (!params.moderatorsToAdd && !params.emailsToRemove) {
      throw new Error('Nenhum moderador para adicionar ou remover.');
    }

    const payload = {
      resource_id: params.resourceId,
      add_moderators: params.moderatorsToAdd,
      remove_moderators_emails: params.emailsToRemove,
    };

    return axios.post(`/plugins/${params.platform}/live/moderators/update`, payload, config)
      .then((response) => {
        if (response.status !== 200) {
          throw new Error(`Erro ao atualizar moderadores: ${response.statusText}`);
        }
        return true;
      })
      .catch((error) => {
        if (error.response && error.response.status !== 401) {
          throw error;
        } else if (error.message) {
          throw error;
        }
      });
  };

  const pluginsDoGetModerators = async (pluginId: string, mediaId: string, pluginType: string) => {
    const config = {
      headers: {
        'Accept-Language': 'pt-BR',
      },
    };

    const queryParams = `?plugin_id=${pluginId}&resource_id=${mediaId}&page=1&limit=50`;
    const endpoint = pluginType === 'facebook' ? '/plugins/my/facebook' : '/plugins/my/instagram';

    return axios.get(`${endpoint}${queryParams}`, config)
      .then((response) => {
        if (response.status === 204) { // No content
          pluginStore.value.plugins.data = [] as Plugin[];
          pluginStore.value.plugins.pagination = {} as Pagination;
          return pluginStore.value.plugins;
        }

        if (response.status !== 200) {
          throw new Error(`Erro ao buscar plugins: ${response.statusText}`);
        }

        const { columns, rows, pagination } = response.data;
        let plugins: Plugin[];

        if (pluginType === 'facebook') {
          plugins = FacebookPlugin.fromApiResponse(columns, rows).map((p) => Plugin.fromObject(p));
        } else {
          plugins = InstagramPlugin.fromApiResponse(columns, rows).map((p) => Plugin.fromObject(p));
        }

        if (plugins.length === 0) {
          return pluginStore.value.plugins;
        }

        pluginStore.value.plugins.data = plugins;
        if (pagination) {
          pluginStore.value.plugins.pagination = new Pagination({
            total: pagination.total,
            perPage: pagination.per_page,
            currentPage: pagination.current_page,
            lastPage: pagination.last_page,
          });
        }
        return pluginStore.value.plugins;
      })
      .catch((error) => {
        if (error.response && error.response.status !== 401) {
          throw error;
        } else if (error.message) {
          throw error;
        }
      });
  };

  const pluginsDoGetMy = async (
    pluginId?: string,
    resourceId?: string,
    pluginType?: string,
    page: number = 1,
    limit: number = 100,
    searchParam: string = '',
    orderBy: string = '',
    order: number = 0,
  ) => {
    const config = {
      headers: {
        'Accept-Language': 'pt-BR',
      },
    };

    const queryParams = [];
    queryParams.push(pluginId ? `plugin_id=${pluginId}` : '');
    queryParams.push(resourceId ? `resource_id=${resourceId}` : '');
    queryParams.push(`page=${page}`);
    queryParams.push(`limit=${limit}`);
    // Server-side account-handle search (Minhas Integrações). Empty term
    // omits the param so the endpoint returns the full paginated list.
    queryParams.push(searchParam ? `search_param=${encodeURIComponent(searchParam)}` : '');
    // Server-side sort. Empty orderBy lets the backend default to name asc.
    queryParams.push(orderBy ? `order_by=${orderBy}` : '');
    queryParams.push(order ? `order=${order}` : '');
    const queryParamsString = queryParams.filter((param) => param.length > 0).join('&');

    const extractPagination = (data: any) => (data?.pagination ? {
      total: data.pagination.total,
      perPage: data.pagination.per_page,
      currentPage: data.pagination.current_page,
      lastPage: data.pagination.last_page,
    } : null);

    // If pluginType is specified, fetch only that type
    if (pluginType) {
      const endpoint = pluginType === 'facebook' ? '/plugins/my/facebook' : '/plugins/my/instagram';
      const response = await axios.get(`${endpoint}?${queryParamsString}`, config);

      if (response.status === 204) {
        return { instagramPlugins: [], facebookPlugins: [], pagination: null };
      }

      if (response.status !== 200) {
        throw new Error(`Erro ao buscar plugins: ${response.statusText}`);
      }

      const { columns, rows } = response.data;
      const pagination = extractPagination(response.data);

      if (pluginType === 'facebook') {
        const facebookPlugins = FacebookPlugin.fromApiResponse(columns, rows);
        return { instagramPlugins: [], facebookPlugins, pagination };
      }
      const instagramPlugins = InstagramPlugin.fromApiResponse(columns, rows);
      return { instagramPlugins, facebookPlugins: [], pagination };
    }

    // Fetch both Instagram and Facebook plugins in parallel
    try {
      const [instagramResponse, facebookResponse] = await Promise.all([
        axios.get(`/plugins/my/instagram?${queryParamsString}`, config),
        axios.get(`/plugins/my/facebook?${queryParamsString}`, config),
      ]);

      const instagramPlugins = instagramResponse.status === 200
        ? InstagramPlugin.fromApiResponse(
          instagramResponse.data.columns,
          instagramResponse.data.rows,
        )
        : [];

      const facebookPlugins = facebookResponse.status === 200
        ? FacebookPlugin.fromApiResponse(
          facebookResponse.data.columns,
          facebookResponse.data.rows,
        )
        : [];

      const instagramPagination = instagramResponse.status === 200
        ? extractPagination(instagramResponse.data)
        : null;
      const facebookPagination = facebookResponse.status === 200
        ? extractPagination(facebookResponse.data)
        : null;

      // Use the pagination with the highest lastPage to drive infinite scroll
      const pagination = [instagramPagination, facebookPagination]
        .filter(Boolean)
        .sort((a, b) => (b?.lastPage ?? 0) - (a?.lastPage ?? 0))[0] || null;

      return { instagramPlugins, facebookPlugins, pagination };
    } catch (error: any) {
      if (error.response && error.response.status !== 401) {
        throw error;
      } else if (error.message) {
        throw error;
      }
      throw error;
    }
  };

  const pluginsDoDelete = async (id: string) => {
    const config = {
      headers: {
        'Accept-Language': 'pt-BR',
      },
    };
    const payload = {
      id,
    };

    return axios.post('/plugins/delete', payload, config)
      .then((response) => {
        if (response.status !== 200) {
          return undefined;
        }
        return true;
      })
      .catch((error) => {
        if (error.response && error.response.status !== 401) {
          throw error;
        } else if (error.message) {
          throw error;
        }
      });
  };

  const pluginsDoCreate = async (plugin: Plugin) => {
    const config = {
      headers: {
        'Accept-Language': 'pt-BR',
      },
    };
    const payload = {
      name: plugin.name,
      type: plugin.type,
      redirect_uri: plugin.redirectURI,
      version: plugin.version,
      token: plugin.token,
      owner_uuid: plugin.ownerUuid,
      admins_uuid: plugin.adminsUuid,
      shopify_oauth: plugin.shopifyOauth ? {
        code: plugin.shopifyOauth.code,
        hmac: plugin.shopifyOauth.hmac,
        host: plugin.shopifyOauth.host,
        shop: plugin.shopifyOauth.shop,
        state: plugin.shopifyOauth.state,
        timestamp: plugin.shopifyOauth.timestamp,
      } : null,
    };

    return axios.post('/plugins/add', payload, config)
      .then((response) => {
        if (response.status !== 200) {
          return undefined;
        }
        return true;
      })
      .catch((error) => {
        if (error.response && error.response.status !== 401) {
          throw error;
        } else if (error.message) {
          throw error;
        }
      });
  };

  const pluginsProcessInstagramToken = async (
    provider: string,
    code: string,
    scope: string,
  ) => {
    const config = {
      headers: {
        'Accept-Language': 'pt-BR',
      },
    };

    const payload = {
      provider,
      code,
      scope,
    };

    return axios.post(
      '/plugins/oauth/exchange',
      payload,
      config,
    ).then(
      (response) => response,
    ).catch((error) => {
      console.error('error', error);
      throw error;
    });
  };

  const pluginsProcessFacebookToken = async (
    provider: string,
    code: string,
    scope: string,
  ) => {
    const config = {
      headers: {
        'Accept-Language': 'pt-BR',
      },
    };

    const payload = {
      provider,
      code,
      scope,
    };

    return axios.post(
      '/plugins/oauth/exchange',
      payload,
      config,
    ).then(
      (response) => response,
    ).catch((error) => {
      console.error('error', error);
      throw error;
    });
  };

  const pluginsProcessManualInstagramToken = async (
    provider: string,
    code: string,
    scope: string,
    account: string,
  ) => {
    const config = {
      headers: {
        'Accept-Language': 'pt-BR',
      },
    };

    const payload = {
      provider,
      code,
      scope,
      account,
    };

    return axios.post(
      '/plugins/oauth/exchange',
      payload,
      config,
    ).then(
      (response) => response,
    ).catch((error) => {
      console.error('error', error);
      throw error;
    });
  };

  const pluginGetInstagramMedias = async (params: {
    pluginId: string,
    page: number,
    limit: number,
  }): Promise<InstagramMediaResponse> => {
    const config = {
      headers: {
        'Accept-Language': 'pt-BR',
      },
    };

    const payload = {
      plugin_id: params.pluginId,
      page: params.page,
      limit: params.limit,
    };

    try {
      const response = await axios.post('/plugins/instagram/medias', payload, config);

      if (response.status === 204) {
        return {
          data: [],
          paging: undefined,
        };
      }

      if (response.status !== 200) {
        throw new Error(`Erro ao buscar mídias: ${response.statusText}`);
      }

      return response.data;
    } catch (error: any) {
      if (error.response && error.response.status !== 401) {
        throw error;
      } else if (error.message) {
        throw new Error(`Erro na solicitação: ${error.message}`);
      } else {
        throw new Error('Erro desconhecido ao buscar mídias.');
      }
    }
  };

  const pluginTranscribeVideo = async (
    params: TranscribeVideoRequest,
  ): Promise<TranscribeVideoResponse> => {
    const config = {
      headers: {
        'Accept-Language': 'pt-BR',
      },
    };

    const payload = {
      video_url: params.video_url,
      media_id: params.media_id,
    };

    try {
      const response = await axios.post('/plugins/transcribe/video', payload, config);

      if (response.status !== 200) {
        throw new Error(`Erro ao iniciar transcrição: ${response.statusText}`);
      }

      return response.data;
    } catch (error: any) {
      if (error.response && error.response.status !== 401) {
        throw error;
      } else if (error.message) {
        throw new Error(`Erro na solicitação: ${error.message}`);
      } else {
        throw new Error('Erro desconhecido ao iniciar transcrição.');
      }
    }
  };

  const pluginGetTranscription = async (
    params: GetTranscriptionRequest,
  ): Promise<GetTranscriptionResponse> => {
    const config = {
      headers: {
        'Accept-Language': 'pt-BR',
      },
    };

    const payload = {
      media_id: params.media_id,
    };

    try {
      const response = await axios.post('/plugins/transcription', payload, config);

      if (response.status === 204) {
        return {
          metadata: null,
          details: [],
        };
      }

      if (response.status !== 200) {
        throw new Error(`Erro ao buscar transcrição: ${response.statusText}`);
      }

      return response.data;
    } catch (error: any) {
      if (error.response && error.response.status !== 401) {
        throw error;
      } else if (error.message) {
        throw new Error(`Erro na solicitação: ${error.message}`);
      } else {
        throw new Error('Erro desconhecido ao buscar transcrição.');
      }
    }
  };

  // ── AI transcription-analysis flow ─────────────────────────────────────
  // Kicks off the agent-controller workflow. Status + results are then read
  // via the polling methods below, which hit pluginsdb collections directly.
  const pluginAnalyzeTranscription = async (
    mediaId: string,
  ): Promise<AnalyzeTranscriptionResponse> => {
    const response = await axios.post(
      '/plugins/transcription/analyze',
      { media_id: mediaId },
    );
    return response.data as AnalyzeTranscriptionResponse;
  };

  // Deterministic "rebuild carts by code" (REDO): rebuilds a code-based
  // live's cart from the active catalog + every comment, per buyer. The
  // admin refuses (409) when the cart was hand-edited unless overwrite is
  // set — the FE confirms first, then re-sends overwrite=true.
  const pluginRebuildCartsByCode = async (params: {
    mediaId: string,
    pluginId: string,
    overwrite?: boolean,
    // true = "import from automation": rebuild the product list from the
    // plugin's active catalog (full shelf + stock) before matching.
    fromCatalog?: boolean,
  }): Promise<Record<string, unknown>> => {
    const response = await axios.post('/plugins/live/products/rebuild-by-code', {
      media_id: params.mediaId,
      plugin_id: params.pluginId,
      overwrite_user_edits: params.overwrite ?? false,
      from_catalog: params.fromCatalog ?? false,
    });
    return response.data as Record<string, unknown>;
  };

  // Returns null when no run has ever been kicked off (HTTP 204). The
  // transcription tab uses this to gate ready-no-analysis vs. analyzing.
  const pluginGetTranscriptionAnalysisStatus = async (
    mediaId: string,
  ): Promise<MediaTranscriptionJobStatus | null> => {
    const response = await axios.get(
      `/plugins/transcription/analyze/status/${encodeURIComponent(mediaId)}`,
      { validateStatus: (s) => s === 200 || s === 204 },
    );
    if (response.status === 204) return null;
    return response.data as MediaTranscriptionJobStatus;
  };

  // Returns null until the first analysis completes for this media.
  const pluginGetTranscriptionAnalysis = async (
    mediaId: string,
  ): Promise<MediaTranscriptionAnalysis | null> => {
    const response = await axios.get(
      `/plugins/transcription/analysis/${encodeURIComponent(mediaId)}`,
      { validateStatus: (s) => s === 200 || s === 204 },
    );
    if (response.status === 204) return null;
    return response.data as MediaTranscriptionAnalysis;
  };

  // Re-run JUST the product-detection chain (no pitch). Triggered from
  // the Carrinhos tab when the seller wants a fresh AI catalog pass.
  // Same response shape as `pluginAnalyzeTranscription`; FE polls the
  // mediaTranscriptionJobs collection for progress.
  // [catalog-refresh] Three things the seller can ask for, and they differ
  // in cost and in what they destroy:
  //   undefined  — re-bind the carts from the catalogue that already
  //                exists. The extraction is served from cache, so it is
  //                cheap and no product changes.
  //   'rebuild'  — re-read the video, then merge: products the host
  //                announced that the list never had appear, and every
  //                edit she made still wins.
  //   'restart'  — re-read the video and DISCARD her working copy. Nothing
  //                brings her prices, hidden products or per-variant stock
  //                back, so the panel only sends it from a confirmation.
  const pluginRematchTranscriptionProducts = async (
    mediaId: string,
    mode?: 'rebuild' | 'restart',
  ): Promise<AnalyzeTranscriptionResponse> => {
    const body: Record<string, unknown> = { media_id: mediaId };
    if (mode) body.mode = mode;
    const response = await axios.post(
      '/plugins/transcription/products/rematch',
      body,
    );
    return response.data as AnalyzeTranscriptionResponse;
  };

  // Active products doc (working copy). Returns null until product
  // detection has produced its first baseline. Carries `is_user_modified`
  // which gates re-runs of the pitch analysis.
  const pluginGetTranscriptionProducts = async (
    mediaId: string,
  ): Promise<MediaTranscriptionProducts | null> => {
    const response = await axios.get(
      `/plugins/transcription/products/${encodeURIComponent(mediaId)}`,
      { validateStatus: (s) => s === 200 || s === 204 },
    );
    if (response.status === 204) return null;
    return response.data as MediaTranscriptionProducts;
  };

  /*
    Hooks
  */
  // Listen to logout event
  // This event is used to clear the store
  eventBus.on('event:logout', resetStore);

  // Load store from localStorage. try/catch so a corrupted / tampered
  // blob doesn't crash every view that imports the store. Only the
  // scalar `csrf` field is worth persisting — the paginated `plugins`
  // list can grow into MB for high-plugin-count sellers and blow the
  // 5 MB quota on every mutation. Fetch on demand instead.
  try {
    const raw = localStorage.getItem('pluginStore');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        pluginStore.value.csrf = parsed.csrf || '';
      }
    }
  } catch (err) {
    console.warn('[pluginStore] corrupted localStorage, resetting', err);
    localStorage.removeItem('pluginStore');
  }

  watch(pluginStore, (pluginsVal) => {
    // Persist ONLY the scalar bits, not the paginated plugin list.
    // Persisting `plugins.data` is what would fill localStorage past
    // its quota and start throwing QuotaExceededError on every write.
    try {
      localStorage.setItem('pluginStore', JSON.stringify({ csrf: pluginsVal.csrf }));
    } catch (err) {
      console.warn('[pluginStore] localStorage write failed', err);
    }
  }, {
    deep: true,
  });

  const pluginDownloadLiveMessages = async (params: {
    pluginId: string,
    resourceId: string,
  }) => {
    const config = {
      headers: {
        'Accept-Language': localStorage.getItem('user-locale') || 'pt-BR',
      },
      responseType: 'blob' as const,
    };

    const payload = {
      plugin_id: params.pluginId,
      resource_id: params.resourceId,
    };

    return axios.post('/plugins/instagram/live/messages/download', payload, config)
      .then((response) => {
        if (response.status !== 200) {
          throw new Error('Erro ao baixar mensagens.');
        }

        const contentDisposition = response.headers['content-disposition'];
        let filename = 'livelink-messages.xlsx';
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
          if (match) {
            [, filename] = match;
          }
        }

        const blob = new Blob([response.data], {
          type: String(response.headers['content-type'] ?? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'),
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        return true;
      })
      .catch((error) => {
        if (error.response && error.response.status !== 401) {
          throw error;
        } else if (error.message) {
          throw error;
        }
      });
  };

  // ── Plugin Bio ────────────────────────────────────────────────────────
  // Same wire shape as user bio — backend mirrors UserBio with `pluginId`
  // instead of `userUuid`. Reuses the UserBio frontend model.
  const pluginGetBio = async (pluginId: string): Promise<UserBio | null> => {
    const config = {
      headers: { 'Accept-Language': 'pt-BR' },
    };
    return axios.get(`/plugins/${pluginId}/bio`, config)
      .then((response) => {
        if (response.status !== 200) return null;
        const { columns, row } = response.data;
        const [bio] = UserBio.fromApiResponse(columns, [row]);
        return bio || null;
      })
      .catch((error) => {
        if (error.response?.status === 404) return null;
        if (error.response && error.response.status !== 401) throw error;
        if (error.message) throw error;
        return null;
      });
  };

  const pluginUpdateBio = async (
    pluginId: string,
    params: UserBio,
  ): Promise<boolean> => {
    const config = {
      headers: { 'Accept-Language': 'pt-BR' },
    };
    const payload = {
      id: params.id,
      style: params.style,
      live: params.live,
      profile: params.profile,
      links: params.links,
      disabled: params.disabled,
      // username is enforced by the backend to match the plugin handle —
      // sending it is harmless but ignored.
    };
    return axios.post(`/plugins/${pluginId}/bio`, payload, config)
      .then((response) => response.status === 200 || response.status === 204)
      .catch((error) => {
        if (error.response && error.response.status !== 401) throw error;
        if (error.message) throw error;
        return false;
      });
  };

  // Public lookup by handle (no `@`). For PluginBioPublicView.
  const pluginGetBioByHandle = async (handle: string): Promise<UserBio | null> => {
    const cleanHandle = handle.replace('@', '');
    const config = {
      headers: { 'Accept-Language': 'pt-BR' },
    };
    return axios.get(`/plugins/bio/${cleanHandle}`, config)
      .then((response) => {
        if (response.status !== 200) return null;
        const { columns, row } = response.data;
        const [bio] = UserBio.fromApiResponse(columns, [row]);
        return bio || null;
      })
      .catch((error) => {
        if (error.response?.status === 404) return null;
        if (error.response && error.response.status !== 401) throw error;
        if (error.message) throw error;
        return null;
      });
  };

  // ── Plugin Settings ───────────────────────────────────────────────────
  // Backs the Livvia "prepare" toggle (auto-DM). Same auth chain as
  // billing-config — session + owner-OR-admin enforced server-side.
  const settingsAxios = createFeatureAxios();
  const pluginGetSettings = async (
    pluginId: string,
  ): Promise<{ settings: any; auto_message_enabled: boolean }> => {
    const response = await settingsAxios.get(`/plugins/${pluginId}/settings`);
    return response.data;
  };

  const pluginUpdateSettings = async (params: {
    pluginId: string;
    autoMessageEnabled?: boolean;
    mode?: 'transcript' | 'catalog' | '';
    onboardingStep?: number;
  }): Promise<any> => {
    // Only send fields the caller actually set — server merges nil = no
    // change (once we upgrade the handler to partial updates). For now the
    // whole PluginSettings subdoc is overwritten, so we include every
    // field to preserve state, using the caller's values where present.
    const payload: Record<string, unknown> = {};
    if (params.autoMessageEnabled !== undefined) {
      payload.auto_message_enabled = params.autoMessageEnabled;
    }
    if (params.mode !== undefined) payload.mode = params.mode;
    if (params.onboardingStep !== undefined) {
      payload.onboarding_step = params.onboardingStep;
    }
    const response = await settingsAxios.put(`/plugins/${params.pluginId}/settings`, payload);
    return response.data;
  };

  return {
    pluginStore,
    resetStore,
    pluginsDoGetMy,
    pluginsDoGetModerators,
    pluginsDoUpdateModerators,
    pluginsDoCreate,
    pluginGetSettings,
    pluginUpdateSettings,
    pluginsDoDelete,
    pluginsProcessInstagramToken,
    pluginsProcessFacebookToken,
    pluginsProcessManualInstagramToken,
    pluginGetInstagramLiveMetadatas,
    pluginGetFacebookLiveMetadatas,
    pluginGetLiveMetadatas,
    pluginGenerateAILiveShoppingCarts,
    pluginGetAILiveShoppingCarts,
    pluginEditLiveProduct,
    pluginAdjustLiveCart,
    pluginGetCartLineEvidence,
    pluginGetBillingConfig,
    pluginUpdateBillingConfig,
    pluginGetLiveCartStatuses,
    pluginSetLiveCartStatus,
    pluginGetCartsAggregate,
    pluginGetLiveMetadatasMetrics,
    pluginGetSharedWithMeLiveMetadatas,
    pluginGetInstagramLiveMessages,
    pluginGetFacebookLiveMessages,
    pluginGetLiveMessagesPeak,
    pluginGetInstagramLiveUsers,
    pluginGetFacebookLiveUsers,
    pluginRefreshLiveMetadata,
    pluginGetInstagramLiveStatus,
    pluginGetFacebookLiveStatus,
    pluginLiveMessageReply,
    pluginGetInstagramMedias,
    pluginTranscribeVideo,
    pluginGetTranscription,
    pluginAnalyzeTranscription,
    pluginRematchTranscriptionProducts,
    pluginRebuildCartsByCode,
    pluginGetTranscriptionAnalysisStatus,
    pluginGetTranscriptionAnalysis,
    pluginGetTranscriptionProducts,
    pluginDownloadLiveMessages,
    pluginGetBio,
    pluginUpdateBio,
    pluginGetBioByHandle,
  };
});
